import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import StepModal from '@swyftx/aviary/molecules/StepModal';
import { UpdateMarginPosition } from '@swyftx/margin/build/types';

import { FiatIdEnum } from '@shared/enums';
import { formatCurrency } from '@shared/utils';

import { SwyftxMarginStalePriceCloseBanner } from '@routes/SwyftxMargin/components/General/SwyftxMarginStalePriceBanner';

import { DateTime } from 'luxon';
import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useUpdateMarginPosition } from 'src/lib/margin/hooks/positions';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions/useMarginPositionDetails';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { MarginInstrument } from '../../../General';

type Props = {
  positionId?: string;
  onClose: () => void;
};

const MarginPositionDetailsClosePositionStep: React.FC<Props> = ({ onClose, positionId }) => {
  const { position, positionMarket, side, priceScale, positionMarketLotsPrecision, profitLoss, commission } =
    useMarginPositionDetails({ positionId });

  const { getAssetById } = useMarkets();
  const aud = useMemo(() => getAssetById(FiatIdEnum.AUD), [getAssetById]);
  const [loading, setLoading] = useState<boolean>(false);
  const { updatePosition, invalidateCache } = useUpdateMarginPosition();
  const { symbol } = useMarginInstrumentDetails({ instrument: positionMarket });
  const { addSnackBar } = useSnackBars();
  const { t } = useTranslation('margin');

  const onUpdatePosition = useCallback(async () => {
    if (!position) return;

    try {
      setLoading(true);

      const positionUpdate: UpdateMarginPosition = {
        closed: DateTime.now().toMillis(),
      };

      await updatePosition(position.positionId, positionUpdate);

      addSnackBar({
        id: 'margin-order-close-success',
        variant: 'success',
        content: position.sell
          ? t('protectionOrder.shortPositionClosed', { symbol })
          : t('protectionOrder.longPositionClosed', { symbol }),
        showIcon: true,
        duration: 5_000,
      });
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: t('protectionOrder.positionFailedClose', { symbol }),
        showIcon: true,
        duration: 5_000,
      });

      invalidateCache();
    } finally {
      setLoading(false);
      onClose();
    }
  }, [addSnackBar, invalidateCache, onClose, position, symbol, t, updatePosition]);

  return (
    <StepModal.Step
      key={MarginPositionDetailsStep.ClosePosition}
      onClose={onClose}
      hideActions
      title='Close position'
      position='dynamic'
    >
      <FlexLayout direction='column' spacing={24} className='h-full px-24 pb-24' justifyContent='space-between'>
        <SwyftxMarginStalePriceCloseBanner />

        <FlexLayout direction='column' spacing={12}>
          <ReviewItem title={t('position.instrument.title')}>
            <MarginInstrument market={positionMarket} size='medium' weight='bold' />
          </ReviewItem>
          {side !== undefined && (
            <ReviewItem title={t('position.side.title')}>
              <Body color='primary' weight='bold' loading={!position}>
                {t(`tradeSide.${side}`)}
              </Body>
            </ReviewItem>
          )}
          <ReviewItem title={t('position.sizeLots.title')}>
            <Numeric color='primary' weight='bold' loading={!position}>
              {formatCurrency(position?.volume, undefined, { priceScale: positionMarketLotsPrecision })}
            </Numeric>
          </ReviewItem>
          {position?.openPrice && (
            <ReviewItem title={t('position.averageFillPrice.title')}>
              <Numeric color='primary' weight='bold' loading={!position}>
                {formatCurrency(position?.openPrice, undefined, { priceScale })}
              </Numeric>
            </ReviewItem>
          )}
          {position?.currentPrice && (
            <ReviewItem title={t('position.currentPrice.title')}>
              <Numeric color='primary' weight='bold' loading={!position}>
                {formatCurrency(position?.currentPrice)}
              </Numeric>
            </ReviewItem>
          )}
          <ReviewItem
            title={t('position.profitLoss.title')}
            underline='dashed'
            tooltip={t('position.profitLoss.tooltip')}
          >
            <Numeric color={profitLoss?.gte(0) ? 'success' : 'error'} weight='bold' loading={!position}>
              {formatCurrency(profitLoss, aud, { appendCode: true, hideCode: false })}
            </Numeric>
          </ReviewItem>

          <Separator className='my-16' />

          <FlexLayout direction='column' spacing={8}>
            <Utility variant='caption' weight='emphasis' color='primary'>
              {t('position.fees.title')}
            </Utility>
            <ReviewItem
              title={t('position.commission.title')}
              underline='dashed'
              tooltip={t('position.commission.tooltip')}
            >
              <Numeric color='primary' weight='bold' loading={!position}>
                {formatCurrency(commission, aud, { appendCode: true, hideCode: false })}
              </Numeric>
            </ReviewItem>
            <ReviewItem title={t('position.overnightFee.title')}>
              <Numeric color='primary' weight='bold' loading={!position}>
                {formatCurrency(position?.swap || '0', aud, { appendCode: true, hideCode: false })}
              </Numeric>
            </ReviewItem>
          </FlexLayout>
        </FlexLayout>
        <Button
          color='destructive'
          data-testid='margin-order-confirm-close-position'
          size='lg'
          loading={loading}
          onClick={onUpdatePosition}
          disabled={!positionMarket?.close}
        >
          {t('position.buttons.close')}
        </Button>
      </FlexLayout>
    </StepModal.Step>
  );
};

export { MarginPositionDetailsClosePositionStep };
